import { useState } from 'react';
import { useMutation, useQuery, gql } from '@/apollo';
import type { Condition, ConditionFormState, ConditionCategory } from '../types/moderator.types';
import { initialConditionState } from '../types/moderator.types';
import { logError } from '../../../utils/errorMessages';

// GraphQL Mutations
const CREATE_CONDITION = gql`
  mutation CreateConditionWithNotes($input: CreateConditionWithNotesInput!) {
    createConditionWithNotes(input: $input) {
      id
      name
      categoryId
      category {
        id
        name
      }
      description
      canCancel
      canReschedule
      notes {
        id
        content
        order
      }
    }
  }
`;

const UPDATE_CONDITION = gql`
  mutation UpdateConditionWithNotes($id: ID!, $input: UpdateConditionWithNotesInput!) {
    updateConditionWithNotes(id: $id, input: $input) {
      id
      name
      categoryId
      category {
        id
        name
      }
      description
      canCancel
      canReschedule
      notes {
        id
        content
        order
      }
    }
  }
`;

const DELETE_CONDITION = gql`
  mutation DeleteConditionWithNotes($id: ID!) {
    deleteConditionWithNotes(id: $id)
  }
`;

const GET_CONDITION_CATEGORIES = gql`
  query GetConditionCategories {
    conditionCategories {
      id
      name
      isActive
    }
  }
`;

const CREATE_CONDITION_CATEGORY = gql`
  mutation CreateConditionCategory($input: CreateConditionCategoryInput!) {
    createConditionCategory(input: $input) {
      id
      name
      isActive
    }
  }
`;

const GET_CONDITION_CATEGORIES_WITH_COUNTS = gql`
  query GetConditionCategoriesWithCounts {
    conditionCategoriesWithCounts {
      id
      name
      isActive
      createdAt
      updatedAt
      conditionCount
    }
  }
`;

const DELETE_CONDITION_CATEGORY = gql`
  mutation DeleteConditionCategory($id: ID!) {
    deleteConditionCategory(id: $id)
  }
`;

export interface ConditionCategoryWithCount {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  conditionCount: number;
}

export interface UseConditionManagementReturn {
  // State
  showForm: boolean;
  editing: Condition | null;
  form: ConditionFormState;
  searchTerm: string;
  newNote: string;
  showDeleteModal: boolean;
  deleteId: string | null;
  categories: ConditionCategory[];
  showNewCategoryInput: boolean;
  newCategoryName: string;
  showManageCategoriesModal: boolean;
  categoriesWithCounts: ConditionCategoryWithCount[];
  
  // State setters
  setShowForm: (show: boolean) => void;
  setEditing: (condition: Condition | null) => void;
  setForm: (form: ConditionFormState) => void;
  setSearchTerm: (term: string) => void;
  setNewNote: (note: string) => void;
  setShowNewCategoryInput: (show: boolean) => void;
  setNewCategoryName: (name: string) => void;
  setShowManageCategoriesModal: (show: boolean) => void;
  
  // Handlers
  handleCreate: () => void;
  handleEdit: (condition: Condition) => void;
  handleDelete: (id: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleCancel: () => void;
  handleAddNote: () => void;
  handleRemoveNote: (index: number) => void;
  handleReorderNotes: (notes: string[]) => void;
  handleCategoryChange: (value: string) => void;
  handleCreateCategory: (name?: string) => Promise<void>;
  handleOpenManageCategories: () => void;
  handleCloseManageCategories: () => void;
  handleDeleteCategory: (id: string) => Promise<void>;
  
  // Loading states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isCreatingCategory: boolean;
  isDeletingCategory: boolean;
}

export const useConditionManagement = (
  refetchConditions: () => void,
  refetchModeratorData: () => void
): UseConditionManagementReturn => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Condition | null>(null);
  const [form, setForm] = useState<ConditionFormState>(initialConditionState);
  const [searchTerm, setSearchTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);

  // Fetch condition categories
  const { data: categoriesData, refetch: refetchCategories } = useQuery<{ conditionCategories: ConditionCategory[] }>(GET_CONDITION_CATEGORIES);
  const categories = categoriesData?.conditionCategories || [];

  // Fetch condition categories with counts for management modal
  const { data: categoriesWithCountsData, refetch: refetchCategoriesWithCounts } = useQuery<{ conditionCategoriesWithCounts: ConditionCategoryWithCount[] }>(GET_CONDITION_CATEGORIES_WITH_COUNTS, {
    skip: !showManageCategoriesModal
  });
  const categoriesWithCounts = categoriesWithCountsData?.conditionCategoriesWithCounts || [];

  const [createCondition, { loading: isCreating }] = useMutation(CREATE_CONDITION, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialConditionState);
      refetchModeratorData();
      refetchConditions();
    },
    onError: (error) => {
      logError(error, 'Create Condition');
    }
  });

  const [updateCondition, { loading: isUpdating }] = useMutation(UPDATE_CONDITION, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialConditionState);
      refetchModeratorData();
      refetchConditions();
    },
    onError: (error) => {
      logError(error, 'Update Condition');
    }
  });

  const [deleteCondition, { loading: isDeleting }] = useMutation(DELETE_CONDITION, {
    onCompleted: () => {
      setShowDeleteModal(false);
      setDeleteId(null);
      refetchModeratorData();
      refetchConditions();
    },
    onError: (error) => {
      logError(error, 'Delete Condition');
    }
  });

  const [createConditionCategory, { loading: isCreatingCategory }] = useMutation(CREATE_CONDITION_CATEGORY, {
    onCompleted: async (data) => {
      // Refetch categories to get the new one
      await refetchCategories();
      await refetchCategoriesWithCounts();
    },
    onError: (error) => {
      logError(error, 'Create Condition Category');
    }
  });

  const [deleteConditionCategory, { loading: isDeletingCategory }] = useMutation(DELETE_CONDITION_CATEGORY, {
    onCompleted: async () => {
      // Refetch categories after deletion
      await refetchCategories();
      await refetchCategoriesWithCounts();
    },
    onError: (error) => {
      logError(error, 'Delete Condition Category');
    }
  });

  const handleCreate = () => {
    setShowForm(true);
    setEditing(null);
    setForm(initialConditionState);
  };

  const handleEdit = (condition: Condition) => {
    setShowForm(true);
    setEditing(condition);
    setForm({
      name: condition.name,
      categoryId: condition.categoryId || '',
      description: condition.description || '',
      notes: condition.notes?.map(n => n.content) || []
    });
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      try {
        await deleteCondition({ variables: { id: deleteId } });
        // Modal will be closed in onCompleted callback
      } catch (error) {
        // Error is already logged in onError callback
        // Don't close modal on error - let user retry or cancel
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let categoryIdToUse = form.categoryId === '__ADD_NEW__' ? null : (form.categoryId || null);

    // If "Add New" is selected and there's a category name, create the category first
    if (form.categoryId === '__ADD_NEW__' && newCategoryName.trim()) {
      try {
        const categoryResult = await createConditionCategory({
          variables: {
            input: {
              name: newCategoryName.trim(),
              isActive: true
            }
          }
        });
        
        if (categoryResult.data?.createConditionCategory) {
          categoryIdToUse = categoryResult.data.createConditionCategory.id;
          await refetchCategories();
          await refetchCategoriesWithCounts();
        }
      } catch (error) {
        // Error is already logged in onError callback
        throw error;
      }
    }

    if (editing) {
      await updateCondition({
        variables: {
          id: editing.id,
          input: {
            name: form.name,
            categoryId: categoryIdToUse,
            description: form.description,
            notes: form.notes
          }
        }
      });
    } else {
      await createCondition({
        variables: {
          input: {
            name: form.name,
            categoryId: categoryIdToUse,
            description: form.description,
            notes: form.notes
          }
        }
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(initialConditionState);
    setNewNote('');
    setShowNewCategoryInput(false);
    setNewCategoryName('');
  };

  const handleCategoryChange = (value: string) => {
    if (value === '__ADD_NEW__') {
      setShowNewCategoryInput(true);
      setForm({ ...form, categoryId: '__ADD_NEW__' });
    } else {
      setShowNewCategoryInput(false);
      setNewCategoryName('');
      setForm({ ...form, categoryId: value });
    }
  };

  const handleCreateCategory = async (name?: string) => {
    // Ensure we have a valid string, not an event object or other type
    const categoryName = typeof name === 'string' ? name.trim() : newCategoryName.trim();
    if (!categoryName) {
      return;
    }

    try {
      const result = await createConditionCategory({
        variables: {
          input: {
            name: categoryName,
            isActive: true
          }
        }
      });
      
      // Only clear form state if called from form (no name parameter)
      if (!name && result?.data?.createConditionCategory) {
        setForm({ ...form, categoryId: result.data.createConditionCategory.id });
        setNewCategoryName('');
        setShowNewCategoryInput(false);
      }
    } catch (error) {
      // Error is already logged in onError callback
      throw error;
    }
  };

  const handleOpenManageCategories = () => {
    setShowManageCategoriesModal(true);
    refetchCategoriesWithCounts();
  };

  const handleCloseManageCategories = () => {
    setShowManageCategoriesModal(false);
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteConditionCategory({
        variables: { id }
      });
    } catch (error) {
      // Error is already logged in onError callback
    }
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setForm({ ...form, notes: [...form.notes, newNote.trim()] });
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setForm({ ...form, notes: form.notes.filter((_, i) => i !== index) });
  };

  const handleReorderNotes = (notes: string[]) => {
    setForm({ ...form, notes });
  };

  return {
    showForm,
    editing,
    form,
    searchTerm,
    newNote,
    showDeleteModal,
    deleteId,
    categories,
    showNewCategoryInput,
    newCategoryName,
    showManageCategoriesModal,
    categoriesWithCounts,
    setShowForm,
    setEditing,
    setForm,
    setSearchTerm,
    setNewNote,
    setShowNewCategoryInput,
    setNewCategoryName,
    setShowManageCategoriesModal,
    handleCreate,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleSubmit,
    handleCancel,
    handleAddNote,
    handleRemoveNote,
    handleReorderNotes,
    handleCategoryChange,
    handleCreateCategory,
    handleOpenManageCategories,
    handleCloseManageCategories,
    handleDeleteCategory,
    isCreating,
    isUpdating,
    isDeleting,
    isCreatingCategory,
    isDeletingCategory,
  };
};

